import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { EmptyState, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { MessageSquarePlus, Bug, Lightbulb, HelpCircle, Heart, Inbox, CheckCircle2, Star } from "lucide-react";
import { toast } from "sonner";

const CAT_META = {
  bug: { icon: Bug, cls: "bg-[#FEF2F2] text-ff-red", label: "Bug" },
  idea: { icon: Lightbulb, cls: "bg-[#FFF7ED] text-ff-amber", label: "Idea" },
  confusing: { icon: HelpCircle, cls: "bg-[#EFF6FF] text-ff-blue", label: "Confusing" },
  praise: { icon: Heart, cls: "bg-[#ECFDF5] text-ff-green", label: "Praise" },
  general: { icon: MessageSquarePlus, cls: "bg-[#F3F4F6] text-ff-dark", label: "General" },
};

const fmt = (iso) => { try { return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return iso; } };

export default function Feedback() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("open");

  const load = () => {
    setItems(null);
    api.get("/admin/feedback", { params: { status: tab } }).then((r) => setItems(r.data)).catch(() => setItems([]));
  };
  useEffect(load, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (id) => {
    try { await api.post(`/admin/feedback/${id}/resolve`); toast.success("Marked resolved."); load(); }
    catch { toast.error("Could not resolve."); }
  };

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Beta Feedback</h1>
        <p className="mt-1 text-sm text-ff-medium">What your testers are telling you — bugs, ideas, confusion, and praise in one place.</p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <div className="-mx-1 overflow-x-auto px-1 pb-1">
          <TabsList className="bg-[#F3F4F6]">
            <TabsTrigger value="open" data-testid="feedback-tab-open">Open</TabsTrigger>
            <TabsTrigger value="resolved" data-testid="feedback-tab-resolved">Resolved</TabsTrigger>
            <TabsTrigger value="all" data-testid="feedback-tab-all">All</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={tab} className="pt-5">
          {items === null ? (
            <InlineLoader label="Loading feedback" />
          ) : items.length === 0 ? (
            <EmptyState icon={Inbox} title="No feedback yet" message="When testers send feedback, it shows up here." testid="feedback-empty" />
          ) : (
            <div className="space-y-3" data-testid="admin-feedback-list">
              {items.map((f) => {
                const m = CAT_META[f.category] || CAT_META.general;
                return (
                  <div key={f.id} className="flex items-start justify-between gap-3 rounded-xl border border-ff-border bg-white p-4 shadow-sm" data-testid={`feedback-row-${f.id}`}>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-bold ${m.cls}`}>
                          <m.icon className="h-3.5 w-3.5" /> {m.label}
                        </span>
                        {f.rating ? (
                          <span className="inline-flex items-center gap-0.5 text-xs font-bold text-ff-amber">
                            {Array.from({ length: f.rating }).map((_, i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}
                          </span>
                        ) : null}
                        {f.status === "resolved" && <span className="rounded-full bg-[#ECFDF5] px-2 py-0.5 text-xs font-bold text-ff-green">resolved</span>}
                        <span className="text-xs text-ff-medium">{fmt(f.created_at)}</span>
                      </div>
                      <div className="mt-2 whitespace-pre-wrap text-sm text-ff-dark">{f.message}</div>
                      <div className="mt-1.5 text-xs text-ff-medium">
                        {f.email ? <a href={`mailto:${f.email}`} className="font-semibold text-ff-blue underline">{f.email}</a> : "anonymous"}
                        {f.page_url ? <> · <a href={f.page_url} target="_blank" rel="noopener noreferrer" className="underline">page</a></> : null}
                      </div>
                    </div>
                    {f.status === "open" && (
                      <Button size="sm" onClick={() => resolve(f.id)} data-testid={`feedback-resolve-${f.id}`}
                        className="shrink-0 bg-ff-green text-white hover:bg-ff-green/90">
                        <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Resolve
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
